/**
 * Runs the quality governor (src/quality/quality.ts) on real frame intervals and
 * applies the result: render scale via R3F's setDpr here; MSAA and bloom via the
 * quality store, which PostFX listens to.
 */
import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useStore } from '@react-three/fiber/webgpu'
import { flags } from '../config/flags'
import { perf } from '../debug/perf'
import { QualityGovernor, ladderAt, describeSettings, effectiveDpr, effectiveKey } from '../quality/quality'
import { fixedLevel, manualOverride, qualityStore } from '../quality/store'

/** Same range the Canvas uses: at least 1 (where the cap allows), at most the ?dpr cap. */
export const baseDpr = () => Math.min(Math.max(window.devicePixelRatio || 1, Math.min(1, flags.maxDpr)), flags.maxDpr)

/** ?qfail (test only) lowers the step-down threshold; hitches and windows scale with it. */
function governorOptions() {
  const failFps = flags.qualityFailFps
  if (failFps === 55) return {}
  const k = failFps / 55
  return { failFps, hardFailFps: 48 * k, settledFailFps: 52 * k, hitchMs: Math.max(250, 15000 / failFps) }
}

export function AdaptiveQuality() {
  const store = useStore()
  const last = useRef(-1)

  const governor = useMemo(() => {
    const postOn = flags.post
    const bloomMatters = postOn && flags.bloom > 0
    return new QualityGovernor((from, to) => {
      const b = baseDpr()
      const a = ladderAt(from)
      const c = ladderAt(to)
      return effectiveKey(b, a, postOn, bloomMatters) === effectiveKey(b, c, postOn, bloomMatters)
    }, governorOptions(), fixedLevel)
  }, [])

  useEffect(() => {
    const onVisibility = () => governor.resetWindow(performance.now(), 500)
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [governor])

  useFrame((state) => {
    const now = performance.now()
    const dt = last.current < 0 ? 16.7 : now - last.current
    last.current = now

    const base = baseDpr()
    const basePixels = state.size.width * state.size.height * base * base
    const level = governor.frame(now, dt, basePixels)
    qualityStore.setLevel(level)

    // Re-applied every frame, so a moved window or browser zoom picks up the new base ratio.
    const target = effectiveDpr(base, qualityStore.get())
    if (Math.abs(state.viewport.dpr - target) > 0.001) store.getState().setDpr(target)

    perf.quality = `L${qualityStore.level()} ${manualOverride ? 'manual' : governor.state === 'fixed' ? 'fixed' : `auto: ${governor.state}${governor.fps ? ` @${governor.fps.toFixed(0)}` : ''}`} · ${flags.post ? describeSettings(qualityStore.get()) : `scale ${qualityStore.get().scale} (post off)`}`
    perf.qualityLog = governor.events.map(
      (e) => `${e.t.toFixed(1)}s L${e.from}→L${e.to} ${e.reason} @${e.fps.toFixed(e.fps < 10 ? 1 : 0)}fps`,
    )
  })

  return null
}
